import type { CanonicalVideoState, VideoSource } from '../../../../shared/protocol';
import type { VideoPlayerRef } from '../../types/video';
import type { PlayerCapabilities } from '../video/VideoProvider';
import type { ServerMessage } from '../../types/websocket';

export interface SyncEngineOptions {
  driftThresholdSeconds?: number;
  checkIntervalMs?: number;
  getPlayerCurrentTime: () => number;
  getPlayerState: () => 'playing' | 'paused' | 'unstarted' | 'buffering' | 'ended' | 'error';
  getPlayerCapabilities?: () => PlayerCapabilities;
  onSeekRequired: (position: number) => void;
  onPlayRequired: () => void;
  onPauseRequired: () => void;
  onLocalCommand?: (type: 'PLAY' | 'PAUSE' | 'SEEK', position: number, clientRevision: number) => void;
  onLocalVideoChange?: (source: VideoSource, clientRevision: number) => void;
}

/**
 * Keeps playback deterministic on the client. Server revisions are the only
 * ordering authority; client revisions are hints and are never trusted.
 */
export class SyncEngine {
  private canonicalState: CanonicalVideoState | null = null;
  private lastRevision = -1;
  private remoteAction = false;
  private suppressedPlaybackState: 'playing' | 'paused' | null = null;
  private suppressedPlaybackUntil = 0;
  private interval: ReturnType<typeof setInterval> | null = null;
  private graceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly options: Required<SyncEngineOptions>;
  private readonly noCapabilities: PlayerCapabilities = { canPlay: true, canPause: true, canSeek: true, canGetPosition: true, canSync: true };

  constructor(options: SyncEngineOptions) {
    this.options = {
      driftThresholdSeconds: options.driftThresholdSeconds ?? 2,
      checkIntervalMs: options.checkIntervalMs ?? 5_000,
      getPlayerCurrentTime: options.getPlayerCurrentTime,
      getPlayerState: options.getPlayerState,
      getPlayerCapabilities: options.getPlayerCapabilities ?? (() => this.noCapabilities),
      onSeekRequired: options.onSeekRequired,
      onPlayRequired: options.onPlayRequired,
      onPauseRequired: options.onPauseRequired,
      onLocalCommand: options.onLocalCommand ?? (() => undefined),
      onLocalVideoChange: options.onLocalVideoChange ?? (() => undefined),
    };
  }

  applyServerMessage(message: Extract<ServerMessage, { type: 'ROOM_STATE' | 'PLAY' | 'PAUSE' | 'SEEK' | 'VIDEO_CHANGE' }>): boolean {
    const state = message.state;
    const isSnapshot = message.type === 'ROOM_STATE';
    if (state.revision < this.lastRevision || (!isSnapshot && state.revision === this.lastRevision)) return false;
    this.lastRevision = state.revision;
    this.canonicalState = state;

    if (message.type === 'VIDEO_CHANGE') {
      this.scheduleGracePeriod();
      return true;
    }
    this.applyCanonicalToPlayer(state);
    this.scheduleGracePeriod();
    return true;
  }

  applyState(state: CanonicalVideoState): boolean {
    if (state.revision <= this.lastRevision) return false;
    this.lastRevision = state.revision;
    this.canonicalState = state;
    this.applyCanonicalToPlayer(state);
    this.scheduleGracePeriod();
    return true;
  }

  /** Call after the iframe becomes ready, including after a video change. */
  synchronizeNow(): void {
    if (this.canonicalState) this.applyCanonicalToPlayer(this.canonicalState);
  }

  getExpectedPosition(now = Date.now()): number {
    if (!this.canonicalState) return 0;
    if (!this.canonicalState.playing) return this.canonicalState.position;
    return this.canonicalState.position + Math.max(0, now - this.canonicalState.updatedAt) / 1000;
  }

  getCanonicalState(): CanonicalVideoState | null {
    return this.canonicalState;
  }

  getLastRevision(): number { return this.lastRevision; }

  requestLocalCommand(type: 'PLAY' | 'PAUSE' | 'SEEK', position: number): boolean {
    const capabilities = this.options.getPlayerCapabilities();
    if (!capabilities.canSync
      || (type === 'PLAY' && !capabilities.canPlay)
      || (type === 'PAUSE' && !capabilities.canPause)
      || (type === 'SEEK' && !capabilities.canSeek)) return false;
    this.options.onLocalCommand(type, position, this.lastRevision);
    return true;
  }

  requestVideoChange(source: VideoSource): void {
    this.options.onLocalVideoChange(source, this.lastRevision);
  }

  start(): void {
    if (this.interval !== null) return;
    this.interval = setInterval(() => this.checkDrift(), this.options.checkIntervalMs);
  }

  stop(): void {
    if (this.interval !== null) clearInterval(this.interval);
    if (this.graceTimer !== null) clearTimeout(this.graceTimer);
    this.interval = null;
    this.graceTimer = null;
  }

  reset(): void {
    this.stop();
    this.canonicalState = null;
    this.lastRevision = -1;
    this.remoteAction = false;
    this.suppressedPlaybackState = null;
    this.suppressedPlaybackUntil = 0;
  }

  isRemoteAction(state?: 'playing' | 'paused' | 'unstarted' | 'buffering' | 'ended' | 'error'): boolean {
    if (this.remoteAction) return true;
    if (state && state === this.suppressedPlaybackState && Date.now() <= this.suppressedPlaybackUntil) {
      this.suppressedPlaybackState = null;
      return true;
    }
    return false;
  }

  withLocalAction(action: () => void): void { action(); }

  private applyCanonicalToPlayer(state: CanonicalVideoState): void {
    const capabilities = this.options.getPlayerCapabilities();
    if (!capabilities.canSync) return;
    const expected = this.getExpectedPosition();
    this.remoteAction = true;
    try {
      const current = capabilities.canGetPosition ? this.options.getPlayerCurrentTime() : null;
      if (capabilities.canSeek && current !== null && Math.abs(current - expected) > this.options.driftThresholdSeconds) {
        this.options.onSeekRequired(expected);
      }
      const playerState = this.options.getPlayerState();
      if (state.playing && capabilities.canPlay && playerState !== 'playing') {
        this.suppressPlaybackNotification('playing');
        this.options.onPlayRequired();
      }
      if (!state.playing && capabilities.canPause && playerState === 'playing') {
        this.suppressPlaybackNotification('paused');
        this.options.onPauseRequired();
      }
    } finally {
      this.remoteAction = false;
    }
  }

  private checkDrift(): number {
    if (!this.canonicalState) return 0;
    const capabilities = this.options.getPlayerCapabilities();
    if (!capabilities.canSync) return 0;
    const playerState = this.options.getPlayerState();
    if (['buffering', 'unstarted', 'ended', 'error'].includes(playerState)) return 0;
    const drift = capabilities.canGetPosition ? this.options.getPlayerCurrentTime() - this.getExpectedPosition() : 0;
    if (capabilities.canSeek && capabilities.canGetPosition && Math.abs(drift) > this.options.driftThresholdSeconds) {
      this.remoteAction = true;
      try { this.options.onSeekRequired(this.getExpectedPosition()); } finally { this.remoteAction = false; }
    }
    if (this.canonicalState.playing && capabilities.canPlay && playerState === 'paused') {
      this.remoteAction = true;
      this.suppressPlaybackNotification('playing');
      try { this.options.onPlayRequired(); } finally { this.remoteAction = false; }
    } else if (!this.canonicalState.playing && capabilities.canPause && playerState === 'playing') {
      this.remoteAction = true;
      this.suppressPlaybackNotification('paused');
      try { this.options.onPauseRequired(); } finally { this.remoteAction = false; }
    }
    return drift;
  }

  private scheduleGracePeriod(): void {
    if (this.graceTimer !== null) clearTimeout(this.graceTimer);
    if (this.interval !== null) clearInterval(this.interval);
    this.interval = null;
    this.graceTimer = setTimeout(() => {
      this.graceTimer = null;
      this.checkDrift();
      this.start();
    }, 2_000);
  }

  private suppressPlaybackNotification(state: 'playing' | 'paused'): void {
    this.suppressedPlaybackState = state;
    this.suppressedPlaybackUntil = Date.now() + 1_500;
  }
}

export type { VideoPlayerRef };
